import { createContext, useContext, useState, useMemo, useCallback } from 'react';
import {
  getFromLocalStorage,
  setToLocalStorage,
  validatePreferences,
} from '../utils/localStorageUtils';

// Create the UserPreferences Context
const UserPreferencesContext = createContext(undefined);

// LocalStorage key for user preferences persistence
const PREFERENCES_STORAGE_KEY = 'homeUserPreferences';

// Default preferences configuration
const DEFAULT_PREFERENCES = {
  viewMode: 'grid',
  cardSize: 2,
  cardSpacing: 1.5,
  layoutDensity: 'cozy',
  showDescriptions: true,
  animationsEnabled: true,
  iconSize: 'medium',
  favorites: [],
  pinnedCards: [],
};

/**
 * Loads user preferences from localStorage with validation
 * @returns {Object} Valid preferences object or default preferences
 */
const loadPreferencesFromStorage = () => {
  const savedPreferences = getFromLocalStorage(PREFERENCES_STORAGE_KEY, null);

  if (savedPreferences && validatePreferences(savedPreferences)) {
    console.log('Loaded user preferences from localStorage');
    return savedPreferences;
  }

  if (savedPreferences) {
    console.warn('Invalid preferences found in localStorage. Using defaults.');
  }

  return DEFAULT_PREFERENCES;
};

/**
 * Saves user preferences to localStorage
 * @param {Object} preferences - The preferences object to save
 * @returns {boolean} True if save was successful
 */
const savePreferencesToStorage = (preferences) => {
  if (!validatePreferences(preferences)) {
    console.error('Attempted to save invalid preferences. Save aborted.');
    return false;
  }

  const success = setToLocalStorage(PREFERENCES_STORAGE_KEY, preferences);
  
  if (success) {
    console.log('Saved user preferences to localStorage');
  }

  return success;
};

/**
 * UserPreferencesProvider component that manages user preferences state and persistence
 * Provides preferences context to all child components
 */
export const UserPreferencesProvider = ({ children }) => {
  // Initialize preferences state with value from localStorage or defaults
  const [preferences, setPreferences] = useState(() => loadPreferencesFromStorage());

  /**
   * Updates user preferences (partial or full update)
   * @param {Object} updates - Partial preferences object with fields to update
   */
  const updatePreferences = useCallback((updates) => {
    setPreferences((prevPreferences) => {
      const newPreferences = {
        ...prevPreferences,
        ...updates,
      };

      // Validate before saving
      if (validatePreferences(newPreferences)) {
        savePreferencesToStorage(newPreferences);
        return newPreferences;
      } else {
        console.error('Invalid preference update attempted:', updates);
        return prevPreferences;
      }
    });
  }, []);

  /**
   * Resets all preferences to default values
   */
  const resetPreferences = useCallback(() => {
    console.log('Resetting preferences to defaults');
    setPreferences(DEFAULT_PREFERENCES);
    savePreferencesToStorage(DEFAULT_PREFERENCES);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      preferences,
      updatePreferences,
      resetPreferences,
    }),
    [preferences, updatePreferences, resetPreferences]
  );

  return (
    <UserPreferencesContext.Provider value={contextValue}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

/**
 * Custom hook to consume user preferences context
 * @returns {Object} Context value with preferences, updatePreferences, and resetPreferences
 * @throws {Error} If used outside of UserPreferencesProvider
 */
export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);

  if (context === undefined) {
    console.error(
      'useUserPreferences hook was called outside of UserPreferencesProvider. ' +
      'Ensure the component is wrapped with UserPreferencesProvider.'
    );
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }

  return context;
};
